import type { InputHTMLAttributes } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@ai-tutor/ui';

/**
 * A 16px checkbox with a 44px tap area.
 *
 * A native checkbox cannot take the app's `tap-target`: that class works by
 * an `::after`, and an `<input>` is a replaced element with no pseudo-elements.
 * Wrapping it does not help either, since a tap on the wrapper is not a tap on
 * the box. So the real input is laid INVISIBLY over a 44px square centred on a
 * drawn box: the finger lands on the input itself, keyboard focus and the
 * label's `htmlFor` work as for any checkbox, and the drawn box follows its
 * state through `peer-*`.
 *
 * Neighbours need (44 - 16) / 2 = 14px of clearance on each side, or two tap
 * areas overlap.
 */
export function TapCheckbox({
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <span className={cn('relative flex h-4 w-4 shrink-0', className)}>
      <input
        type='checkbox'
        {...props}
        className='peer absolute left-1/2 top-1/2 z-10 h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-pointer appearance-none rounded-md opacity-0'
      />
      <span
        aria-hidden='true'
        className={cn(
          'pointer-events-none flex h-4 w-4 items-center justify-center rounded-[4px] border border-input bg-background text-primary-foreground transition-colors duration-fast',
          'peer-checked:border-primary peer-checked:bg-primary [&>svg]:invisible peer-checked:[&>svg]:visible',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background'
        )}
      >
        <Check className='h-3 w-3' strokeWidth={3} />
      </span>
    </span>
  );
}
